import { createSlice } from "@reduxjs/toolkit";
import { STORYBOARD_HYDRATION_STATUS } from "@/utils/constants";
import { track } from "@/utils/events/tracker";
import {
  extractApplicationStoryboardUrl,
  resolveAllCssCustomProperties,
} from "@/utils/iframeUtils";
import { TempoElement } from "@/utils/tempoElement";
import { useDevServerStore } from "./zustand/devServerStore";

export enum IFRAME_STATE {
  LOADING = "LOADING",
  SHOWING = "SHOWING",
  HIDDEN = "HIDDEN",
}

// Matches the interface in devtools
export enum FIXED_IFRAME_MESSAGE_IDS {
  HOVERED_ELEMENT_KEY = "HOVERED_ELEMENT_KEY",
  SELECTED_ELEMENT_KEY = "SELECTED_ELEMENT_KEY",
  MULTI_SELECTED_ELEMENT_KEYS = "MULTI_SELECTED_ELEMENT_KEYS",
  CONTEXT_REQUESTED = "CONTEXT_REQUESTED",
  WHEEL_EVENT = "WHEEL_EVENT",
  NAV_TREE = "NAV_TREE",
  NEW_OUTER_HTML_ONLY = "NEW_OUTER_HTML_ONLY",
  KEY_DOWN_EVENT = "KEY_DOWN_EVENT",
  KEY_UP_EVENT = "KEY_UP_EVENT",
  MOUSE_MOVE_EVENT = "MOUSE_MOVE_EVENT",
  DRAG_START_EVENT = "DRAG_START_EVENT",
  DRAG_END_EVENT = "DRAG_END_EVENT",
  DRAG_CANCEL_EVENT = "DRAG_CANCEL_EVENT",
  LATEST_HREF = "LATEST_HREF",
  LATEST_HYDRATION_ERROR_STATUS = "LATEST_HYDRATION_ERROR_STATUS",
  START_EDITING_TEXT = "START_EDITING_TEXT",
  EDITED_TEXT = "EDITED_TEXT",
  INSTANT_UPDATE_DONE = "INSTANT_UPDATE_DONE",
  EDIT_DYNAMIC_TEXT = "EDIT_DYNAMIC_TEXT",
  COMPONENT_PREVIEW_CAPTURED = "COMPONENT_PREVIEW_CAPTURED",
  COMPONENT_PREVIEW_ERROR = "COMPONENT_PREVIEW_ERROR",
  CSS_CUSTOM_PROPERTIES = "CSS_CUSTOM_PROPERTIES",
  ERROR = "ERROR",
  SCROLL_POSITION = "SCROLL_POSITION",
  HEARTBEAT = "HEARTBEAT",
}
const FIXED_IFRAME_MESSAGE_ID_VALUES = new Set(
  Object.values(FIXED_IFRAME_MESSAGE_IDS),
);

export const iframeSlice = createSlice({
  name: "iframe",
  initialState: {
    iframeChangedKey: 0, // Doesn't reload the iframe, but notifies that it is likely that it changed

    // Dev server info
    canvasIdToDevServerUrl: {} as Record<string, string>,

    // Specific known iframe message states
    hoveredElementKey: null,
    selectedElementKey: null,

    multiSelectedElementKeys: null, // In multiselect mode this is an array and contains the selected element key as one of the elements

    showContextMenu: false,

    // Files that have been modified and need to be reloaded
    filesToReload: {},

    // TODO: we want to remove this when we figure out how to trigger drop events on the iframe
    draggingComponent: false,

    // Fetched from the iframe
    storyboardIdToHref: {},

    messageResponse: null,
    cssCustomProperties: null as {
      root: { [key: string]: string };
      dark: { [key: string]: string };
    } | null,
  },
  reducers: {
    setIFrameChanged: (state) => {
      state.iframeChangedKey = state.iframeChangedKey + 1;
    },

    setHoveredElementKey: (state, action) => {
      if (action.payload instanceof TempoElement) {
        // @ts-expect-error TS2322
        state.hoveredElementKey = action.payload.getKey();
        return;
      }

      state.hoveredElementKey = action.payload;
    },
    setSelectedElementKey: (state, action) => {
      // If this is called always reset the multi-select state as we are selecting a single element
      state.multiSelectedElementKeys = null;

      if (action.payload instanceof TempoElement) {
        // @ts-expect-error TS2322
        state.selectedElementKey = action.payload.getKey();
        return;
      }

      state.selectedElementKey = action.payload;
    },
    setMultiSelectWithElementKey: (state, action) => {
      let newSelectedElementKey = action.payload;
      if (newSelectedElementKey instanceof TempoElement) {
        newSelectedElementKey = newSelectedElementKey.getKey();
      }

      // Case 1: if there is nothing selected, just select this element
      if (!state.selectedElementKey) {
        state.selectedElementKey = newSelectedElementKey;
        return;
      }

      let newMultiselectedElementKeys: string[] = [];

      const newSelectedElement = TempoElement.fromKey(newSelectedElementKey);
      const currentSelectedElement = TempoElement.fromKey(
        state.selectedElementKey,
      );

      // Case 2: there is something to deselect from the multiselect keys
      const elementToDeselect = state.multiSelectedElementKeys
        // @ts-expect-error TS2339
        ?.map((elementKey: string) => {
          return TempoElement.fromKey(elementKey);
        })
        .find((element: TempoElement) => {
          return (
            element.isParentOf(newSelectedElement) ||
            element.isEqual(newSelectedElement)
          );
        });
      if (elementToDeselect) {
        // @ts-expect-error TS2339
        newMultiselectedElementKeys = state.multiSelectedElementKeys?.filter(
          (elementKey) =>
            !elementToDeselect.isEqual(TempoElement.fromKey(elementKey)),
        );

        // If deselecting the main selected element, pick a new one to be the main selected element
        // Note that if there is only 1 element left there is logic further down to handle that case
        if (
          elementToDeselect.getKey() === state.selectedElementKey &&
          newMultiselectedElementKeys.length > 1
        ) {
          // @ts-expect-error TS2322
          state.selectedElementKey = newMultiselectedElementKeys[0];
        }

        // Case 3: This element is a sibling of the currently selected element
      } else if (newSelectedElement.isSiblingOf(currentSelectedElement)) {
        // @ts-expect-error TS2339
        if (state.multiSelectedElementKeys?.length) {
          newMultiselectedElementKeys = [
            ...state.multiSelectedElementKeys,
            newSelectedElementKey,
          ];
        } else {
          newMultiselectedElementKeys = [
            currentSelectedElement.getKey(),
            newSelectedElementKey,
          ];
        }
      } else {
        // Just return, since this is not an element that can be added or removed from multiselect
        return;
      }

      if (newMultiselectedElementKeys.length > 1) {
        // @ts-expect-error TS2322
        state.multiSelectedElementKeys = newMultiselectedElementKeys;
        return;
      }

      // Now handle the new multiselected keys after changes
      // If only one left in multiselect mode, just select that one and exit the multiselect mode
      if (newMultiselectedElementKeys.length === 1) {
        // @ts-expect-error TS2322
        state.selectedElementKey = newMultiselectedElementKeys[0];
        state.multiSelectedElementKeys = null;
        return;
      }

      // Empty case, just exit, shouldn't happen but here just in case
      state.multiSelectedElementKeys = null;
    },
    setAllSelectedElementKeys: (state, action) => {
      state.multiSelectedElementKeys = action.payload.multiSelectedElementKeys;
      state.selectedElementKey = action.payload.selectedElementKey;
    },
    setShowContextMenu: (state, action) => {
      state.showContextMenu = action.payload;
    },
    setMessageResponse: (state, action) => {
      if (!action.payload.id) {
        return;
      }

      // If this is a fixed message, just set the exact state
      if (FIXED_IFRAME_MESSAGE_ID_VALUES.has(action.payload.id)) {
        switch (action.payload.id) {
          case FIXED_IFRAME_MESSAGE_IDS.CSS_CUSTOM_PROPERTIES: {
            // Resolve all CSS custom properties recursively when storing them
            if (
              action.payload.cssProperties.root !== undefined ||
              action.payload.cssProperties.dark !== undefined
            ) {
              // New nested format
              const { root = {}, dark = {} } = action.payload.cssProperties;
              state.cssCustomProperties = {
                root: resolveAllCssCustomProperties(root),
                dark: resolveAllCssCustomProperties(dark),
              };
            }
            break;
          }
          case FIXED_IFRAME_MESSAGE_IDS.LATEST_HYDRATION_ERROR_STATUS: {
            const messagesToSkip = [
              "Uncaught TypeError: Cannot read properties of null (reading 'useState')",
              "Uncaught TypeError: Cannot read properties of null (reading 'useContext')",
              "Uncaught TypeError: Cannot read properties of null (reading 'useMemo')",
              "Uncaught TypeError: Cannot read properties of null (reading 'useRef')",
            ];

            const skipErrorMessage =
              action.payload.errorBody &&
              messagesToSkip.some((message) => {
                return action.payload.errorBody.includes(message);
              });

            if (skipErrorMessage) {
              track("STORYBOARD_SKIP_ERROR", {
                error: action.payload,
                type: "hydration",
              });
              return;
            }

            if (
              action.payload.status !== STORYBOARD_HYDRATION_STATUS.NO_ERROR
            ) {
              track("STORYBOARD_ERROR", {
                error: action.payload,
                type: "hydration",
              });

              useDevServerStore.getState().addTerminalErrors([
                {
                  message: action.payload.errorBody,
                  filename: action.payload.errorDescr,
                  stack: action.payload.errorLabel,
                  count: 1,
                },
              ]);
            }
            break;
          }
        }

        return;
      }
    },

    setDevServerUrl: (state, action) => {
      state.canvasIdToDevServerUrl[action.payload.canvasId] =
        action.payload.url;
    },
    setDraggingComponent: (state, action) => {
      state.draggingComponent = action.payload;
    },
    resetCssCustomProperties: (state) => {
      state.cssCustomProperties = null;
    },
    clearSelection: (state) => {
      state.selectedElementKey = null;
      state.multiSelectedElementKeys = null;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setIFrameChanged,
  setMessageResponse,
  setDevServerUrl,
  setHoveredElementKey,
  setSelectedElementKey,
  setMultiSelectWithElementKey,
  setShowContextMenu,
  setDraggingComponent,
  setAllSelectedElementKeys,
  resetCssCustomProperties,
  clearSelection,
} = iframeSlice.actions;

export default iframeSlice.reducer;
